import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';

import Box from '../box/box.jsx';
import PeripheralTile from './peripheral-tile.jsx';
import Dots from './dots.jsx';

import radarIcon from './icons/searching.png';
import refreshIcon from './icons/refresh.svg';

import styles from './connection-modal.css';

const UsbStatusLine = ({usbChecking, usbPortCount, usbError, onRequestUsbPort}) => {
    if (usbChecking) {
        return (
            <div className={styles.usbStatusLine}>
                <FormattedMessage
                    defaultMessage="Checking USB (Web Serial)..."
                    description="Status while checking USB ports"
                    id="gui.connection.scanning.usbChecking"
                />
            </div>
        );
    }
    if (usbError === 'not_supported') {
        return (
            <div className={styles.usbStatusLine}>
                <FormattedMessage
                    defaultMessage="USB status: Web Serial not supported in this browser."
                    description="Web Serial API not available"
                    id="gui.connection.scanning.usbNotSupported"
                />
            </div>
        );
    }
    if (usbError) {
        return (
            <div className={styles.usbStatusLine}>
                <FormattedMessage
                    defaultMessage="USB (Web Serial): {error}"
                    description="Error checking USB ports"
                    id="gui.connection.scanning.usbError"
                    values={{error: usbError}}
                />
            </div>
        );
    }
    if (typeof usbPortCount === 'number') {
        if (usbPortCount > 0) {
            return (
                <div className={styles.usbStatusLine}>
                    <FormattedMessage
                        defaultMessage="USB (Web Serial): {count} port(s) available. You can use Upload to flash the board."
                        description="USB ports already granted"
                        id="gui.connection.scanning.usbPortsAvailable"
                        values={{count: usbPortCount}}
                    />
                </div>
            );
        }
        return (
            <div className={styles.usbStatusLine}>
                <p className={styles.usbStatusText}>
                    <FormattedMessage
                        defaultMessage="Board plugged in but not yet selected. Click the button below to choose the USB port."
                        description="No USB port granted - instruct to select"
                        id="gui.connection.scanning.usbSelectPortPrompt"
                    />
                </p>
                {typeof onRequestUsbPort === 'function' && (
                    <button
                        type="button"
                        className={styles.connectionButton}
                        onClick={onRequestUsbPort}
                    >
                        <FormattedMessage
                            defaultMessage="Select USB port"
                            description="Button to open browser port picker"
                            id="gui.connection.scanning.usbSelectPortButton"
                        />
                    </button>
                )}
            </div>
        );
    }
    return null;
};

const ScanningStep = props => (
    <Box className={styles.body}>
        {props.isSerialport ? (
            <Box className={classNames(styles.bodyHeadArea)}>
                <div className={styles.listAll}>
                    <FormattedMessage
                        defaultMessage="Show all connectable devices"
                        description="Button in prompt for show all connectable devices"
                        id="gui.connection.scanning.listAll"
                    />
                </div>
                <div className={styles.checkBox}>
                    <input
                        type="checkbox"
                        name="hexform"
                        checked={props.isListAll}
                        onChange={props.onClickListAll}
                    />
                </div>
            </Box>
        ) : null}
        {props.isSerialport && (props.usbChecking || typeof props.usbPortCount === 'number' || props.usbError) ? (
            <UsbStatusLine
                usbChecking={props.usbChecking}
                usbPortCount={props.usbPortCount}
                usbError={props.usbError}
                onRequestUsbPort={props.onRequestUsbPort}
            />
        ) : null}
        <Box className={styles.activityArea}>
            {props.scanning ? (
                props.peripheralList.length === 0 ? (
                    <div className={styles.activityAreaInfo}>
                        <div className={styles.centeredRow}>
                            <img
                                className={classNames(styles.radarSmall, styles.radarSpin)}
                                src={radarIcon}
                            />
                            <FormattedMessage
                                defaultMessage="Looking for devices"
                                description="Text shown while scanning for devices"
                                id="gui.connection.scanning.lookingforperipherals"
                            />
                        </div>
                    </div>
                ) : (
                    <div className={styles.peripheralTilePane}>
                        {props.peripheralList.map(peripheral =>
                            (<PeripheralTile
                                connectionSmallIconURL={props.connectionSmallIconURL}
                                key={peripheral.peripheralId}
                                name={peripheral.name}
                                peripheralId={peripheral.peripheralId}
                                rssi={peripheral.rssi}
                                isSerialport={props.isSerialport}
                                onConnecting={props.onConnecting}
                            />)
                        )}
                    </div>
                )
            ) : (
                <Box className={styles.instructions}>
                    <FormattedMessage
                        defaultMessage="No devices found"
                        description="Text shown when no devices could be found"
                        id="gui.connection.scanning.noPeripheralsFound"
                    />
                </Box>
            )}
        </Box>
        <Box className={styles.bottomArea}>
            <Box className={classNames(styles.bottomAreaItem, styles.instructions)}>
                <FormattedMessage
                    defaultMessage="Select your device in the list above."
                    description="Prompt for choosing a device to connect to"
                    id="gui.connection.scanning.instructions"
                />
            </Box>
            <Dots
                className={styles.bottomAreaItem}
                counter={0}
                total={3}
            />
            <button
                className={classNames(styles.bottomAreaItem, styles.connectionButton)}
                onClick={props.onRefresh}
            >
                <FormattedMessage
                    defaultMessage="Refresh"
                    description="Button in prompt for starting a search"
                    id="gui.connection.search"
                />
                <img
                    className={styles.buttonIconRight}
                    src={refreshIcon}
                />
            </button>
        </Box>
    </Box>
);

ScanningStep.propTypes = {
    connectionSmallIconURL: PropTypes.string,
    isListAll: PropTypes.bool.isRequired,
    isSerialport: PropTypes.bool,
    onClickListAll: PropTypes.func.isRequired,
    onConnecting: PropTypes.func,
    onRefresh: PropTypes.func,
    peripheralList: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        rssi: PropTypes.number,
        peripheralId: PropTypes.string
    })),
    scanning: PropTypes.bool.isRequired,
    usbPortCount: PropTypes.number,
    usbError: PropTypes.string,
    usbChecking: PropTypes.bool,
    onRequestUsbPort: PropTypes.func
};

ScanningStep.defaultProps = {
    peripheralList: [],
    scanning: true
};

export default ScanningStep;
